import type { Session } from "@webyonetim/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toDataURL } from "qrcode";
import { useEffect, useState, type SyntheticEvent } from "react";

import { PageHeader } from "../../components/shell/AppShell";
import { Button, CopyButton, PasswordField, TextField } from "../../components/ui/Controls";
import {
  AlertBanner,
  ErrorState,
  InlineResult,
  Skeleton,
  StatusBadge,
} from "../../components/ui/Feedback";
import {
  DestructiveConfirmDialog,
  RecoveryCodePanel,
  ReauthenticationDialog,
  SecuritySection,
  type ReauthenticationCredentials,
} from "../../components/ui/SecurityDialogs";
import { apiErrorMessage, useAuth } from "../../lib/auth";
import { formatDateTime } from "../../lib/format";
import { securityApi, type TotpSetupStartResponse } from "../../lib/api";
import { SessionList } from "./SessionList";

type SensitiveAction = "disable-totp" | "regenerate-codes" | "revoke-others";

export function SecurityPage() {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const statusQuery = useQuery({
    queryKey: ["security-status"],
    queryFn: securityApi.status,
    retry: false,
  });
  const sessionsQuery = useQuery({
    queryKey: ["sessions"],
    queryFn: securityApi.sessions,
    retry: false,
  });
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [passwordResult, setPasswordResult] = useState<string>();
  const [passwordError, setPasswordError] = useState<string>();
  const [totpSetup, setTotpSetup] = useState<TotpSetupStartResponse>();
  const [qrDataUrl, setQrDataUrl] = useState<string>();
  const [totpCode, setTotpCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState<readonly string[]>();
  const [action, setAction] = useState<SensitiveAction>();
  const [reauthenticationId, setReauthenticationId] = useState<string>();
  const [actionError, setActionError] = useState<string>();
  const [revokeSessionTarget, setRevokeSessionTarget] = useState<Session>();
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (totpSetup === undefined) return;
    let active = true;
    void toDataURL(totpSetup.provisioningUri, { margin: 2, width: 240 }).then((url) => {
      if (active) setQrDataUrl(url);
    });
    return () => {
      active = false;
    };
  }, [totpSetup]);
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["security-status"] });
    void queryClient.invalidateQueries({ queryKey: ["sessions"] });
    void queryClient.invalidateQueries({ queryKey: ["identity"] });
  };
  const changePassword = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    setPasswordError(undefined);
    setPasswordResult(undefined);
    try {
      const result = await securityApi.changePassword({
        currentPassword,
        newPassword,
        newPasswordConfirmation: confirmation,
      });
      setPasswordResult(
        result.otherSessionsRevoked
          ? "Parola değiştirildi ve diğer oturumlar sonlandırıldı."
          : "Parola değiştirildi. Diğer oturumlar açık kaldı.",
      );
      refresh();
    } catch (caught) {
      setPasswordError(apiErrorMessage(caught));
    } finally {
      setCurrentPassword("");
      setNewPassword("");
      setConfirmation("");
      setBusy(false);
    }
  };
  const startTotp = async () => {
    setBusy(true);
    setActionError(undefined);
    try {
      setTotpSetup(await securityApi.startTotp());
    } catch (caught) {
      setActionError(apiErrorMessage(caught));
    } finally {
      setBusy(false);
    }
  };
  const enableTotp = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (totpSetup === undefined) return;
    setBusy(true);
    setActionError(undefined);
    try {
      const result = await securityApi.enableTotp({
        credentialId: totpSetup.credentialId,
        code: totpCode,
      });
      setRecoveryCodes(result.recoveryCodes);
      setTotpSetup(undefined);
      setTotpCode("");
      refresh();
    } catch (caught) {
      setActionError(apiErrorMessage(caught));
      setTotpCode("");
    } finally {
      setBusy(false);
    }
  };
  const verify = async (credentials: ReauthenticationCredentials) => {
    setBusy(true);
    setActionError(undefined);
    try {
      const result = await securityApi.reauthenticate({
        password: credentials.password,
        ...(credentials.secondFactor === undefined
          ? {}
          : {
              secondFactor: /^\d{6}$/u.test(credentials.secondFactor)
                ? { method: "totp" as const, code: credentials.secondFactor }
                : { method: "recovery_code" as const, code: credentials.secondFactor },
            }),
      });
      if (action === "regenerate-codes") {
        const codes = await securityApi.recoveryCodes(result.reauthenticationId);
        setRecoveryCodes(codes.recoveryCodes);
        setAction(undefined);
        refresh();
      } else setReauthenticationId(result.reauthenticationId);
    } catch (caught) {
      setActionError(apiErrorMessage(caught));
    } finally {
      setBusy(false);
    }
  };
  const confirmSensitiveAction = async () => {
    if (action === undefined || reauthenticationId === undefined) return;
    setBusy(true);
    setActionError(undefined);
    try {
      if (action === "disable-totp") await securityApi.disableTotp(reauthenticationId);
      if (action === "revoke-others") await securityApi.revokeOthers(reauthenticationId);
      setAction(undefined);
      setReauthenticationId(undefined);
      refresh();
    } catch (caught) {
      setActionError(apiErrorMessage(caught));
    } finally {
      setBusy(false);
    }
  };
  const revokeSession = async () => {
    if (revokeSessionTarget === undefined) return;
    setBusy(true);
    setActionError(undefined);
    try {
      await securityApi.revokeSession(revokeSessionTarget.id);
      setRevokeSessionTarget(undefined);
      refresh();
    } catch (caught) {
      setActionError(apiErrorMessage(caught));
    } finally {
      setBusy(false);
    }
  };
  const closeSensitive = () => {
    setAction(undefined);
    setReauthenticationId(undefined);
    setActionError(undefined);
  };
  const actionName =
    action === "disable-totp"
      ? "İki adımlı doğrulamayı kapat"
      : action === "regenerate-codes"
        ? "Kurtarma kodlarını yenile"
        : "Diğer tüm oturumları sonlandır";
  const otherSessionCount =
    sessionsQuery.data?.sessions.filter(
      (session) => !session.current && session.status === "active",
    ).length ?? 0;
  return (
    <div className="page">
      <PageHeader
        description="Parolanızı, iki adımlı doğrulamayı, kurtarma kodlarını ve oturumları yönetin."
        title="Güvenlik"
      />
      {statusQuery.isLoading || sessionsQuery.isLoading ? <Skeleton /> : null}
      {statusQuery.isError || sessionsQuery.isError ? <ErrorState onRetry={refresh} /> : null}
      {statusQuery.data === undefined ? null : (
        <div className="stack">
          <SecuritySection
            description={`Son değişiklik: ${formatDateTime(statusQuery.data.passwordChangedAt)}`}
            title="Parola"
          >
            <form
              className="form-stack"
              onSubmit={(event) => {
                void changePassword(event);
              }}
            >
              <PasswordField
                autoComplete="current-password"
                label="Mevcut parola"
                onChange={(event) => {
                  setCurrentPassword(event.currentTarget.value);
                }}
                required
                value={currentPassword}
              />
              <PasswordField
                autoComplete="new-password"
                label="Yeni parola"
                minLength={12}
                onChange={(event) => {
                  setNewPassword(event.currentTarget.value);
                }}
                required
                value={newPassword}
              />
              <PasswordField
                autoComplete="new-password"
                label="Yeni parolayı doğrula"
                minLength={12}
                onChange={(event) => {
                  setConfirmation(event.currentTarget.value);
                }}
                required
                value={confirmation}
              />
              {passwordError === undefined ? null : (
                <InlineResult tone="error">{passwordError}</InlineResult>
              )}
              {passwordResult === undefined ? null : <InlineResult>{passwordResult}</InlineResult>}
              <Button busy={busy} busyLabel="Parola değiştiriliyor…" type="submit">
                Parolayı değiştir
              </Button>
            </form>
          </SecuritySection>
          <SecuritySection title="İki adımlı doğrulama">
            {statusQuery.data.totpEnabled ? (
              <>
                <StatusBadge label="Etkin" status="active" />
                <p>Hesabınız parola ve ikinci faktörle korunuyor.</p>
                <div className="form-actions">
                  <Button
                    onClick={() => {
                      setAction("regenerate-codes");
                    }}
                    variant="secondary"
                  >
                    Kurtarma kodlarını yenile
                  </Button>
                  <Button
                    onClick={() => {
                      setAction("disable-totp");
                    }}
                    variant="destructive"
                  >
                    İki adımlı doğrulamayı kapat
                  </Button>
                </div>
              </>
            ) : (
              <>
                <AlertBanner heading="İki adımlı doğrulama etkin değil" tone="warning">
                  <p>Hesabınızı parola ele geçirilmesine karşı korumak için etkinleştirin.</p>
                </AlertBanner>
                {totpSetup === undefined ? (
                  <Button
                    busy={busy}
                    busyLabel="Hazırlanıyor…"
                    onClick={() => {
                      void startTotp();
                    }}
                  >
                    İki adımlı doğrulamayı etkinleştir
                  </Button>
                ) : (
                  <form
                    className="form-stack"
                    onSubmit={(event) => {
                      void enableTotp(event);
                    }}
                  >
                    {qrDataUrl === undefined ? (
                      <Skeleton />
                    ) : (
                      <img
                        alt="Kimlik doğrulama uygulaması için QR kodu"
                        height="240"
                        src={qrDataUrl}
                        width="240"
                      />
                    )}
                    <div className="card">
                      <code>{totpSetup.manualKey}</code>
                      <CopyButton label="Kurulum anahtarını kopyala" value={totpSetup.manualKey} />
                    </div>
                    <TextField
                      autoComplete="one-time-code"
                      inputMode="numeric"
                      label="6 haneli doğrulama kodu"
                      maxLength={6}
                      onChange={(event) => {
                        setTotpCode(event.currentTarget.value);
                      }}
                      required
                      value={totpCode}
                    />
                    <Button busy={busy} busyLabel="Etkinleştiriliyor…" type="submit">
                      İki adımlı doğrulamayı etkinleştir
                    </Button>
                  </form>
                )}
              </>
            )}
          </SecuritySection>
          {recoveryCodes === undefined ? null : (
            <RecoveryCodePanel
              codes={recoveryCodes}
              onClose={() => {
                setRecoveryCodes(undefined);
              }}
            />
          )}
          <SecuritySection title="Oturumlar">
            {sessionsQuery.data === undefined ? null : (
              <>
                <SessionList
                  onRevoke={setRevokeSessionTarget}
                  sessions={sessionsQuery.data.sessions}
                />
                {otherSessionCount > 0 ? (
                  <Button
                    onClick={() => {
                      setAction("revoke-others");
                    }}
                    variant="destructive"
                  >
                    Diğer tüm oturumları sonlandır
                  </Button>
                ) : null}
              </>
            )}
          </SecuritySection>
        </div>
      )}
      {action === undefined ? null : reauthenticationId === undefined ? (
        <ReauthenticationDialog
          actionName={actionName}
          error={actionError}
          onOpenChange={(next) => {
            if (!next) closeSensitive();
          }}
          onVerify={verify}
          open
          requireSecondFactor={auth.identity?.user.totpEnabled === true}
          submitting={busy}
        />
      ) : (
        <DestructiveConfirmDialog
          consequence={
            action === "disable-totp"
              ? "Hesabınız yalnızca parolayla korunacak. Bu işlem mevcut kurtarma kodlarını da geçersiz kılar."
              : `${String(otherSessionCount)} diğer etkin oturum hemen sonlandırılacak.`
          }
          error={actionError}
          finalAction={actionName}
          name={actionName}
          onConfirm={confirmSensitiveAction}
          onOpenChange={(next) => {
            if (!next) closeSensitive();
          }}
          open
          submitting={busy}
        />
      )}
      {revokeSessionTarget === undefined ? null : (
        <DestructiveConfirmDialog
          consequence={`“${revokeSessionTarget.deviceLabel}” oturumu hemen sonlandırılacak.`}
          error={actionError}
          finalAction="Oturumu sonlandır"
          name="Oturumu sonlandır"
          onConfirm={revokeSession}
          onOpenChange={(next) => {
            if (!next) setRevokeSessionTarget(undefined);
          }}
          open
          submitting={busy}
        />
      )}
    </div>
  );
}
